import { AttachedFile } from '@/components/workspace/types';
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

const BASE_URL = '/pitch-deck-builder';
const BLANK_DECK_URL = '/blank-deck/projects';
const FOLDERS_URL = '/folders';
const SHARE_URL = '/share';

// ============ Request/Response Types ============

interface GetProjectsParams {
    page?: number;
    limit?: number;
    search?: string;
    industry?: string;
    presentationType?: string;
    folderId?: string;
    deckType?: string;
}

interface Project {
    _id: string;
    folderId: string | null;
    name: string;
    industry: string;
    companyStage: string;
    presentationType?: string;
    projectType?: string;
    creditsUsed: number;
    createdAt: string;
    updatedAt: string;
    status: string;
    hasAnalytics: boolean;
    hasSlides: boolean;
    currentStep?: 'companyDetails' | 'coreContext' | 'qanda' | 'outline' | 'theme-selection' | 'editor';
}

interface GetProjectsResponse {
    statusCode: number;
    data: {
        projects: Project[];
        total: number;
    };
    message: string;
    success: boolean;
}

interface Folder {
    _id: string;
    name: string;
    description?: string;
    logo?: string;
    parentFolderId?: string | null;
    isDefault?: boolean;
    sortOrder?: number;
    createdAt: string;
    updatedAt: string;
}

interface GetFoldersResponse {
    statusCode: number;
    data: Folder[];
    message: string;
    success: boolean;
}

export interface ChatHistoryMessage {
    id: string;
    role: "user" | "assistant";
    content: string;
    createdAt: string;
    timestamp?: string;
    suggestions?: string[];
    actions?: any;
    attachment?: any;
}

export interface GetChatHistoryResponse {
    statusCode: number;
    data: {
        messages: ChatHistoryMessage[];
        total: number;
        hasMore: boolean;
    };
    message: string;
    success: boolean;
}

interface ApiResponse {
    statusCode: number;
    data: any;
    message: string;
    success: boolean;
}

interface ShareDeckParams {
    projectId: string;
    emails: string[];
    message?: string;
    contactNumber?: string;
    linkedin?: string;
}

// ============ Comment Types ============

interface DeckComment {
    _id: string;
    visitorId: string;
    visitorEmail: string | null;
    text: string;
    createdAt: string;
}

interface GetDeckCommentsResponse {
    statusCode: number;
    data: {
        comments: DeckComment[];
        total: number;
    };
    message: string;
    success: boolean;
}

interface AddDeckCommentResponse {
    statusCode: number;
    data: {
        comment: DeckComment;
    };
    message: string;
    success: boolean;
}

interface EditDeckCommentResponse {
    statusCode: number;
    data: {
        comment: DeckComment;
    };
    message: string;
    success: boolean;
}

interface DeleteDeckCommentResponse {
    statusCode: number;
    message: string;
    success: boolean;
}

interface MoveDeckParams {
    projectId: string;
    folderId: string | null;
}

interface UpdateProjectParams {
    projectId: string;
    name?: string;
    industry?: string;
    companyStage?: string;
    presentationType?: string;
    currentStep?: string;
}

interface CreateFolderParams {
    name: string;
    description?: string;
    parentFolderId?: string;
    logo?: File;
}

interface UpdateFolderParams {
    folderId: string;
    name?: string;
    description?: string;
    logo?: File;
}

interface SaveCompanyDetailsParams {
    companyName: string;
    companyStage: string;
    industry: string;
    website: string;
    numberOfSlides: string;
    presentationType: string;
}

interface CreateBlankDeckParams {
    documentType?: string;
    companyName?: string;
    templateId?: string;
    companyStage?: string;
    industry?: string;
    website?: string;
    numberOfSlides?: string;
    presentationType?: string;
    deckType?: string;
    userPrompt?: string;
    files?: File[];
    knowledgeBaseIds?: string[];
    logo?: File;
}

interface ReportIssueParams {
    projectId: string;
    reportType: string;
    comment: string;
    slideIndex?: number;
    screenshots?: File[];
}

interface ReportIssueResponse {
    statusCode: number;
    message: string;
    success: boolean;
}

interface CreateBlankDeckResponse {
    statusCode: number;
    data: {
        _id: string;
        documentType?: string;
        deckType?: string;
        presentationType?: string;
        coreContext?: {
            processingStatus?: 'pending' | 'processing' | 'completed' | 'failed';
            fileUrls?: string[];
            fileSources?: Array<{
                url?: string;
                fileName?: string;
                mimeType?: string;
                source?: 'upload' | 'knowledge_base';
            }>;
        };
    };
    message: string;
    success: boolean;
}

interface UpdateCoreContextPromptParams {
    projectId: string;
    prompt: string;
    files?: File[];
    knowledgeBaseIds?: string[];
}



interface ShareVisitor {
    email: string;
    sharedAt: string;
    hasViewed: boolean;
    lastViewedAt?: string;
}

interface GetShareVisitorsResponse {
    statusCode: number;
    data: {
        visitors: ShareVisitor[];
    };
    message: string;
    success: boolean;
}

// ============ Competitor Analysis Types ============
interface CompetitorAnalysisDetail {
    positioning: string;
    pricing: string;
    strengths: string[];
    weaknesses: string[];
}

interface CompetitorItem {
    name: string;
    website: string;
    analysis: CompetitorAnalysisDetail;
}

interface ComparisonFeature {
    featureName: string;
    currentCompany: string;
    competitors: Record<string, string | boolean>;
}

interface CompetitiveAdvantage {
    advantage: string;
    impact: string;
    competitors: string[];
}

interface CompetitorMarketMap {
    leaders: string[];
    challengers: string[];
    niche: string[];
}

interface CompetitorSummary {
    totalCompetitors: number;
    marketPosition: string;
    keyDifferentiators: string[];
    strategicAssessment: string;
}

export interface CompetitorAnalysisResult {
    competitors: CompetitorItem[];
    marketMap?: CompetitorMarketMap;
    comparisonTable: {
        features: ComparisonFeature[];
        competitiveAdvantages: CompetitiveAdvantage[];
    };
    summary: string | CompetitorSummary;
    analyzedAt?: string;
    companyContext?: {
        name: string;
        industry: string;
        stage: string;
    };
}

interface SaveMarketResearchParams {
    projectId: string;
    data: MarketAnalysisResult;
}

interface SaveMarketResearchResponse {
    statusCode: number;
    data: any;
    message: string;
    success: boolean;
}

interface GenerateCompetitorAnalysisParams {
    projectId: string;
    companyName: string;
    industry: string;
    website: string;
    companyStage?: string;
    presentationType?: string; // Added as per user request
}

export interface MarketAnalysisResult {
    // Top-level metadata
    companyName?: string;
    website?: string | null;
    presentationType?: string | null;

    // 1. Market Research Section
    marketResearch: {
        marketSize: {
            tam: string;
            sam: string;
            som: string;
            cagr: string;
            forecastYear: string;
        };
        trends: {
            title: string;
            description: string;
            impact: 'High' | 'Medium' | 'Low';
        }[];
        drivers: string[];
        challenges: string[];
        similarPlatformsAnalyzed?: string[];
        summary: string;
    };

    // 2. Competitor Analysis Section
    competitorAnalysis?: CompetitorAnalysisResult;
}

interface GenerateCompetitorAnalysisResponse {
    statusCode: number;
    data: {
        competitorData: CompetitorAnalysisResult;
        competitorAnalysisCompleted: boolean;
        competitorsAnalyzed?: number;
    };
    message: string;
    success: boolean;
}

// ============ Asset Types ============

interface Asset {
    _id: string;
    filename: string;
    url: string;
    size: number;
    mimeType: string;
    type: 'ai' | 'local';
    createdAt: string;
    updatedAt: string;
}

interface GetAssetsParams {
    search?: string;
    type?: 'ai' | 'local';
}

interface GetAssetsResponse {
    statusCode: number;
    data: {
        assets: Asset[];
    };
    message: string;
    success: boolean;
}

interface UploadAssetsParams {
    images: File[];
}

interface RenameAssetParams {
    assetId: string;
    name: string;
}

// ============ Analytics Types ============

interface AnalyticsOverviewItem {
    projectId: string;
    deckName: string;
    totalViews: number;
    avgTimeSpent: number; // seconds
    totalDownloads: number;
    totalTimeSpent: number; // seconds
    createdAt?: string;
}

interface GetAnalyticsOverviewResponse {
    statusCode: number;
    data: AnalyticsOverviewItem[];
    message: string;
    success: boolean;
}

interface SlideEngagementData {
    slideId: string;
    slideTitle: string;
    timeSpent: number; // seconds
}

interface VisitorLocation {
    country?: string;
    city?: string;
    region?: string;
}

interface VisitorInsight {
    visitorId: string;
    name: string;
    email: string | null;
    isShared: boolean;
    device: string;
    location: VisitorLocation | null;
    lastActive: string;
    timeSpent: number;
    sharedAt: string | null;
}

// Summary for aggregated analytics (all visitors)
interface ProjectAnalyticsSummary {
    totalViews: number;
    avgTimeSpent: number;
    totalDownloads: number;
    totalTimeSpent: number;
}

// Summary for single visitor analytics
interface VisitorAnalyticsSummary {
    totalTimeSpent: number;
    totalDownloads: number;
    totalEvents: number;
}

// Visitor info returned when filtering by visitorId
interface VisitorInfo {
    visitorId: string;
    name: string;
    device: string;
    ipAddress?: string;
    location?: VisitorLocation;
    startTime: string;
    lastActive: string;
}

// Event data for visitor-specific analytics
interface AnalyticsEvent {
    type: 'view' | 'download' | 'share' | 'contact';
    timestamp: string;
    metadata?: Record<string, unknown>;
}

interface GetProjectAnalyticsResponse {
    statusCode: number;
    data: {
        // Aggregated response fields
        summary?: ProjectAnalyticsSummary | VisitorAnalyticsSummary;
        slidesEngagement?: SlideEngagementData[];
        visitorInsights?: VisitorInsight[];
        // Visitor-specific response fields
        visitorInfo?: VisitorInfo;
        slideEngagement?: SlideEngagementData[];
        events?: AnalyticsEvent[];
    };
    message: string;
    success: boolean;
}

// ============ Team Member Types ============

interface TeamMember {
    userId?: string;
    email: string;
    fullName: string;
    access: 'viewer' | 'editor';
}

interface GetTeamMembersResponse {
    statusCode: number;
    data: {
        teamMembers: TeamMember[];
    };
    message: string;
    success: boolean;
}

interface InviteTeamMembersParams {
    projectId: string;
    teamMembers: { email: string; access?: string }[];
}

// ============ QA Types ============

interface QAAnswer {
    text: string;
    source: string;
    confidence: number;
    references: string[];
}

interface QAQuestion {
    id: string;
    section: string;
    sectionTitle?: string;
    field: string;
    question: string;
    answer: QAAnswer;
    status: 'answered' | 'pending';
    isAnswered: boolean;
}

// ============ Investor QA Types ============

export interface InvestorQAItem {
    questionId: string;
    question: string;
    answer: string;
    category?: string;
    status?: 'answered' | 'unanswered' | 'deferred' | 'edited' | 'pending';
    source?: 'user' | 'document_extraction' | 'rag' | '';
    lastUpdated?: string;
}

interface GetInvestorQAResponse {
    statusCode: number;
    data: {
        investorQandA: InvestorQAItem[];
    };
    message: string;
    success: boolean;
}

interface QAStats {
    totalFields: number;
    answered: number;
    unanswered: number;
    completionPercentage: number;
}

interface StartQaAnswerResponse {
    statusCode: number;
    data: {
        sessionId: string;
        stage: string;
        stats: QAStats;
        questions: QAQuestion[];
        applicableSections: string[];
        message: string;
    };
    message: string;
    success: boolean;
}

interface SubmitQaAnswersParams {
    projectId: string;
    answers: { questionId: string; answer: string }[];
}

interface SubmitQaAnswersResponse {
    statusCode: number;
    data: {
        updatedCount: number;
        questions: QAQuestion[];
    };
    message: string;
    success: boolean;
}

// ============ Outline Types ============

// Individual key point structure
export interface KeyPoint {
    level: string;
    content: string;
}

// Citation type for research sources
interface Citation {
    text: string;
    url?: string | null;
}

// Citation summary for the entire outline
interface CitationSummary {
    totalCitations: number;
    citations: Citation[];
    formatted?: string | null;
}

// Research utilization metrics
interface ResearchUtilization {
    score: number;
    label?: string | null;
    factsUsed: number;
    citationsAdded: number;
}

// Narrative metadata for enhanced flow
interface NarrativeMetadata {
    pattern?: string | null;
    storyArc?: {
        beginning?: string;
        middle?: string;
        end?: string;
    } | null;
    basedOnIntent?: {
        topic?: string | null;
        objective?: string | null;
        audience?: string | null;
    };
}

interface OutlineSlide {
    order: number;
    sectionId: string;
    slideType: string;
    title: string;
    summary?: string;
    keyPoints?: KeyPoint[];
    dataFields?: string[];
    estimatedDuration?: string;
    // Research citations for this slide
    citations?: Citation[];
}

interface OutlineSlideMismatch {
    type: 'more_data_than_slides' | 'less_data_than_slides';
    requested: number;
    suggested?: number;
    generated?: number;
    action: string;
}

// Source from research/citations
interface OutlineSource {
    title: string;
    url?: string | null;
    domain?: string | null;
    isUserProvided?: boolean;
    category?: string | null;
    snippet?: string | null;
}

interface Outline {
    slides: OutlineSlide[];
    narrativeArc?: string;
    totalSlides?: number;
    suggestedSlides?: number;
    deckDuration?: string;
    slideAllocationReasoning?: string;
    tone?: string;
    language?: string;
    sectionsWithInsufficientData?: string[];
    sectionsToHighlight?: string[];
    generatedAt?: string;
    version?: number;
    slideMismatch?: OutlineSlideMismatch;
    // Research citation summary (from deep research pipeline)
    citationSummary?: CitationSummary;
    // Research utilization metrics
    researchUtilization?: ResearchUtilization;
    // Narrative metadata for enhanced flow
    narrativeMetadata?: NarrativeMetadata;
    // Sources list from citationIndex (for display at bottom)
    sources?: OutlineSource[];
}

interface OutlineMeta {
    agent: string;
    version: string;
    tone: string;
    language: string;
    generatedAt: string;
    cached?: boolean;
}

interface OutlineDataSources {
    sectionsWithData: string[];
    sectionsNeedingContent: string[];
    qaAnswersUsed: number;
}

interface OutlineSlideSuggestion {
    type: 'expand_deck' | 'need_more_data';
    requested: number;
    suggested?: number;
    generated?: number;
    difference: number;
    message: string;
    actions: string[];
    sectionsNeedingData?: string[];
}

interface OutlineRecommendation {
    nextStep: string;
    reason: string;
}

interface GenerateOutlineParams {
    projectId: string;
    regenerate?: boolean;
    tone?: 'professional' | 'storytelling' | 'conversational' | 'persuasive';
    language?: string;
}

interface GenerateOutlineResponse {
    success: boolean;
    statusCode: number;
    data: {
        meta: OutlineMeta;
        outline: Outline;
        dataSources: OutlineDataSources;
        slideSuggestion?: OutlineSlideSuggestion;
        recommendation: OutlineRecommendation;
    };
    message: string;
}

interface GetOutlineResponse {
    success: boolean;
    statusCode: number;
    data: {
        exists: boolean;
        outline: Outline | null;
        projectInfo: {
            name: string;
            industry: string;
            stage: string;
        };
        message?: string;
    };
    message: string;
}

interface UpdateOutlineParams {
    projectId: string;
    slides?: Partial<OutlineSlide>[];
    narrativeArc?: string;
    language?: string;
    tone?: string;
}

interface UpdateOutlineResponse {
    success: boolean;
    statusCode: number;
    data: {
        outline: Outline;
        message: string;
    };
    message: string;
}

// AI Slide Modification Types
interface AiModifySlideParams {
    projectId: string;
    slideIndex: number;
    prompt: string;
    currentContent: {
        title: string;
        summary: string;
        keyPoints: KeyPoint[];
    };
}

interface AiModifySlideResponse {
    success: boolean;
    statusCode: number;
    data: {
        title: string;
        summary: string;
        keyPoints: KeyPoint[];
    };
    message: string;
}

// Translate Slide Types (for incremental regeneration)
interface TranslateSlideParams {
    projectId: string;
    slideIndex: number;
    currentContent: {
        title: string;
        summary: string;
        keyPoints: KeyPoint[];
    };
    language: string;
    tone: string;
}

interface TranslateSlideResponse {
    success: boolean;
    statusCode: number;
    data: {
        slideIndex: number;
        title: string;
        summary: string;
        keyPoints: KeyPoint[];
    };
    message: string;
}

// AI Generate Slide From Prompt Types (Enhanced with image, research, context support)
interface GenerateSlideFromPromptParams {
    projectId: string;
    prompt: string;
    imageUrl?: string;              // Optional: S3 URL of user-uploaded image
    imageMetadata?: ImageMetadata;  // Optional: Client-extracted image dimensions
    options?: {
        useOutlineContext?: boolean;      // Load existing slides for context awareness
        allowResearch?: boolean;          // Enable research data fetching
        allowImageGeneration?: boolean;   // Enable AI image generation
    };
}

interface GenerateSlideFromPromptResponse {
    success: boolean;
    statusCode: number;
    data: {
        htmlContent: string;
        title: string;
        slideType: string;
        isFallback: boolean;
    };
    message: string;
}

// AI Edit Slide Types (for editing generated HTML slides)
type EditType = 'TEXT' | 'STYLE' | 'LAYOUT' | 'CONTENT' | 'AUTO';

// Image metadata for intelligent layout decisions
interface ImageMetadata {
    width: number;
    height: number;
    aspectRatio: number;
    type: 'logo' | 'square' | 'landscape' | 'panoramic' | 'portrait';
    fileSize: number;
}

interface AiEditSlideParams {
    projectId: string;
    slideId: string;
    slideIndex: number;
    prompt: string;
    currentHtmlContent: string;
    attachedFiles?: AttachedFile[];
    editType?: EditType;  // Optional: TEXT, STYLE, LAYOUT, CONTENT, AUTO
    chipId?: string;       // Optional: Pre-configured suggestion chip ID
    imageUrl?: string;     // Optional: S3 URL of user-uploaded image to incorporate
    imageMetadata?: ImageMetadata;  // Optional: Client-extracted image dimensions for layout
}

interface AiEditSlideResponse {
    success: boolean;
    statusCode: number;
    data: {
        htmlContent: string;
        changesSummary?: string;
    };
    message: string;
}

// Suggestion Chips Types
interface SuggestionChip {
    id: string;
    label: string;
    type: EditType;
}

interface GetSuggestionChipsResponse {
    success: boolean;
    statusCode: number;
    data: {
        chips: SuggestionChip[];
    };
    message: string;
}

// Change Slide Layout Types
interface ChangeSlideLayoutParams {
    projectId: string;
    slideId: string;
    slideIndex: number;
    targetLayoutId: string;
    currentHtmlContent: string;
}

interface ChangeSlideLayoutResponse {
    success: boolean;
    statusCode: number;
    data: {
        htmlContent: string;
        layoutId: string;
    };
    message: string;
}

// Background Image Upload Types
interface UploadBackgroundImageParams {
    projectId: string;
    slideId: string;
    image: File;
}

interface UploadBackgroundImageResponse {
    success: boolean;
    statusCode: number;
    data: {
        imageUrl: string;
        key: string;
    };
    message: string;
}

// Generate Background Image Types
interface GenerateBackgroundImageParams {
    projectId: string;
    slideId: string;
    slideIndex: number;
}

interface GenerateBackgroundImageResponse {
    success: boolean;
    statusCode: number;
    data: {
        imageUrl: string;
        key: string;
    };
    message: string;
}

// Generate AI Image Types (for modal preview - returns base64 for preview, no S3 upload)
interface GenerateAiImageParams {
    projectId: string;
    prompt: string;
    aspectRatio: '16:9' | '4:3' | '1:1' | '9:16';
    style: 'Photoreal' | 'Sketch' | '3D Render' | 'Cinematic';
}

interface GenerateAiImageResponse {
    success: boolean;
    statusCode: number;
    data: {
        imageBase64: string;  // Base64 encoded image data (no S3 upload yet)
        mimeType: string;
        creditsUsed: number;
    };
    message: string;
}

// Confirm AI Image Types (uploads base64 to S3 when user clicks "Add to Slide")
interface ConfirmAiImageParams {
    projectId: string;
    imageBase64: string;
}

interface ConfirmAiImageResponse {
    success: boolean;
    statusCode: number;
    data: {
        imageUrl: string;
        key: string;
    };
    message: string;
}

// Apply Background Image Types
interface ApplyBackgroundImageParams {
    projectId: string;
    slideId: string;
    slideIndex: number;
    imageUrl: string;
    currentHtmlContent: string;
}

interface ApplyBackgroundImageResponse {
    success: boolean;
    statusCode: number;
    data: {
        htmlContent: string;
    };
    message: string;
}

// Change Background Color Types
interface ChangeBackgroundColorParams {
    projectId: string;
    slideId: string;
    slideIndex: number;
    color: string;
}

interface ChangeBackgroundColorResponse {
    success: boolean;
    statusCode: number;
    data: {
        htmlContent: string;
    };
    message: string;
}

// Change Theme Types
interface ChangeThemeParams {
    projectId: string;
    brandKitId?: string;
    applyToAll: boolean;
    slideId?: string;
    slideIndex?: number;
}

interface ChangeThemeResponse {
    success: boolean;
    statusCode: number;
    data: {
        slides: Array<{
            _id: string;
            htmlContent: string;
        }>;
    };
    message: string;
}

// ============ Slide Generation Types ============

interface GeneratedSlide {
    _id?: string;
    id?: string;
    order?: number;
    sectionId?: string;
    slideType?: string;
    title: string;
    layout?: string;
    html?: string;
    htmlContent?: string;
    thumbnailUrl?: string | null;
    imageUrls?: string[];
    generatedAt?: string;
}

interface SlideGenerationMeta {
    status: 'completed' | 'failed' | 'partial';
    agent: string;
    version: string;
    totalSlides: number;
    successfulSlides: number;
    failedSlides: number;
    processingTimeMs?: number;
    generatedAt: string;
}

interface GenerateSlidesParams {
    projectId: string;
    regenerate?: boolean;
}

interface GenerateSlidesResponse {
    success: boolean;
    statusCode: number;
    data: {
        meta: SlideGenerationMeta;
        slides: GeneratedSlide[];
        errors?: Array<{
            slideIndex: number;
            error: string;
        }>;
    };
    message: string;
}

interface GetGeneratedSlidesResponse {
    success: boolean;
    statusCode: number;
    data: {
        exists: boolean;
        slides: GeneratedSlide[] | null;
        totalSlides?: number;
        projectInfo?: {
            name: string;
            industry: string;
            stage: string;
            presentationType?: string;
        };
        message?: string;
    };
    message: string;
}

// ============ Market Research Types ============

interface GenerateMarketResearchParams {
    projectId: string; // Added as per user request
    companyName: string;
    industry: string;
    website: string;
    presentationType?: string; // Added as per user request
}

interface MarketSizeData {
    tam: string;
    sam: string;
    som: string;
    cagr: string;
    forecastYear: string;
}

interface MarketTrend {
    title: string;
    description: string;
    impact: 'High' | 'Medium' | 'Low';
}

interface MarketResearchData {
    marketSize: MarketSizeData;
    trends: MarketTrend[];
    drivers: string[];
    challenges: string[];
    summary: string;
}

interface GenerateMarketResearchResponse {
    statusCode: number;
    data: {
        researchData: MarketResearchData;
        researchCompleted: boolean;
    };
    message: string;
    success: boolean;
}

// ============ Platform Template Types ============

export interface SlideTemplate {
    slideType: string;
    variant: string | null;
    thumbnailUrl?: string | null;
    layoutHint?: string;
    maxKeyPoints?: number;
    maxSummaryChars?: number;
    htmlTemplate?: string;
}

export interface DocumentTemplate {
    docxUrl: string;
    thumbnailUrl: string;
    originalFileName: string;
}

export interface PlatformTemplate {
    _id: string;
    name: string;
    slug: string;
    description: string;
    category: string;
    thumbnailUrl: string | null;
    applicablePresentationTypes?: string[];
    sortOrder: number;
    disableTheme: boolean;
    slideTemplates: SlideTemplate[];
    documentTemplate?: DocumentTemplate;
    createdAt?: string;
    updatedAt?: string;
}

interface GetTemplatesResponse {
    success: boolean;
    count: number;
    data: PlatformTemplate[];
}

interface AssignTemplateParams {
    projectId: string;
    templateId: string | null;
}

// ============ Stream-State Types ============

interface StreamStateWorkflowStep {
    completed: boolean;
    status: 'completed' | 'in_progress' | 'pending' | 'skipped';
    completedAt?: string;
}

interface StreamStateWorkflowStatus {
    // Steps is a keyed object: { context, research, outline, slides, ... }
    steps: Record<string, StreamStateWorkflowStep>;
    progress: number; // 0-100
    overallStatus: 'completed' | 'in_progress' | 'pending';
    currentNode?: string | null;
    currentTaskDetails?: {
        progress?: number;
        nodeTitle?: string;
        stepDescription?: string;
        agent?: string;
        message?: string;
    };
}

interface StreamStateCoreContext {
    userPrompt?: string;
    fileUrls?: string[];
    fileSources?: Array<{
        url?: string;
        fileName?: string;
        mimeType?: string;
        source?: 'upload' | 'knowledge_base';
    }>;
    processingStatus?: 'pending' | 'processing' | 'completed' | 'failed';
    deepResearch?: boolean;
    projectName?: string;
}

export interface GetStreamStateResponse {
    statusCode: number;
    success: boolean;
    message: string;
    data: {
        currentStep: string | null;
        processingStatus: string | null;
        workflowStatus: StreamStateWorkflowStatus | null;
        coreContext: StreamStateCoreContext | null;
        nodeResults?: Record<string, any> | null;
        outline: Outline | null;
        hasSlides: boolean;
        slidesCount: number;
        slides?: any[];
        hasDeepResearch: boolean;
        chatHistory: ChatHistoryMessage[];
        name: string;
        documentType?: string;
        projectType?: string;
        presentationType?: string;
        generatedDocument?: any;
        // Fundraising Pitch Deck Q&A fields
        questionsAndAnswers?: Array<{
            questionId: string;
            question: string;
            answer: string;
            status: string;
            category?: string;
            source?: string;
            sectionTitle?: string;
            sectionSubtitle?: string;
        }>;
        hasQA?: boolean;
        qaSessionData?: any;
        companyStage?: string;
        industry?: string;
        website?: { url: string };
        hasAnalytics?: boolean;
        totalViews?: number;
        totalDownloads?: number;
        // Research data
        researchPdfUrl?: string;
        deepResearch?: any;
        citations?: Array<{ index: number; title: string; url: string; domain: string; type: string }>;
        // Citation index from fast research / grounding (Blank Deck)
        citationIndex?: {
            sources?: Array<{ id?: string; title: string; url: string; domain?: string; type?: string; sourceType?: string }>;
        };
        // Pitch Preparation Guide (Fundraising Pitch Deck only)
        pitchPrepDocument?: {
            url: string;
            generatedAt: string;
            version: number;
        } | null;
        myAccess?: 'owner' | 'editor' | 'viewer' | 'admin' | null;
    };
}

// ============ API Definition ============

export const pitchDeckApi = createApi({
    reducerPath: 'pitchDeckApi',
    baseQuery: fetchBaseQuery({
        baseUrl: process.env.NEXT_PUBLIC_API_URL,
        prepareHeaders: (headers, { getState }) => {
            const token = (getState() as any).auth.token;
            if (token) {
                headers.set('authorization', `Bearer ${token}`);
            }
            return headers;
        },
    }),
    tagTypes: ['Project', 'Folder', 'ShareVisitors', 'Asset', 'Analytics', 'TeamMembers', 'ResearchHistory', 'CompetitorHistory', 'PlatformTemplate', 'ChatHistory', 'DeckComments'],
    endpoints: (builder) => ({
        // ============ Project Endpoints ============

        getAllProjects: builder.query<GetProjectsResponse, GetProjectsParams | void>({
            query: (params) => {
                const queryParams = new URLSearchParams();
                if (params?.page) queryParams.append('page', params.page.toString());
                if (params?.limit) queryParams.append('limit', params.limit.toString());
                if (params?.search) queryParams.append('search', params.search);
                if (params?.industry) queryParams.append('industry', params.industry);
                if (params?.presentationType) queryParams.append('presentationType', params.presentationType);
                if (params?.folderId) queryParams.append('folderId', params.folderId);
                if (params?.deckType) queryParams.append('deckType', params.deckType);
                const queryString = queryParams.toString();
                return `${BASE_URL}/projects${queryString ? `?${queryString}` : ''}`;
            },
            providesTags: ['Project'],
            keepUnusedDataFor: 60,
        }),

        getStreamState: builder.query<GetStreamStateResponse, string>({
            query: (projectId) => `/blank-deck/projects/${projectId}/stream-state`,
            providesTags: (_result, _error, projectId) => [
                { type: 'ChatHistory', id: projectId },
                { type: 'Project', id: projectId },
            ],
            keepUnusedDataFor: 0, // Always fetch fresh on workspace load
        }),

        getChatHistory: builder.query<GetChatHistoryResponse, { projectId: string; limit?: number; offset?: number }>({

            query: ({ projectId, limit = 50, offset = 0 }) =>
                `/blank-deck/projects/${projectId}/chat/history?limit=${limit}&offset=${offset}`,
            providesTags: (_result, _error, arg) => [{ type: 'ChatHistory', id: arg.projectId }],
            keepUnusedDataFor: 0, // Always fetch fresh to avoid stale state on reload
        }),

        getProject: builder.query<{ success: boolean; data: Project }, string>({
            query: (projectId) => `${BASE_URL}/projects/${projectId}`,
            providesTags: (_result, _error, id) => [{ type: 'Project', id }],
        }),

        // Rename/Update Project
        updateProject: builder.mutation<ApiResponse, UpdateProjectParams>({
            query: ({ projectId, ...body }) => ({
                url: `${BASE_URL}/update-project/${projectId}`,
                method: 'PUT',
                body,
            }),
            invalidatesTags: ['Project'],
        }),

        // Set Theme Step - Updates currentStep to 'theme'
        setThemeStep: builder.mutation<ApiResponse, string>({
            query: (projectId) => ({
                url: `${BASE_URL}/projects/${projectId}/set-theme-step`,
                method: 'POST',
            }),
            invalidatesTags: ['Project'],
        }),

        // Delete Project
        deleteProject: builder.mutation<ApiResponse, string>({
            query: (projectId) => ({
                url: `${BASE_URL}/delete-project/${projectId}`,
                method: 'DELETE',
            }),
            invalidatesTags: ['Project'],
        }),

        // Bulk Delete Projects
        bulkDeleteProjects: builder.mutation<ApiResponse, { ids: string[] }>({
            query: (body) => ({
                url: `${BASE_URL}/bulk-delete`,
                method: 'DELETE',
                body,
            }),
            invalidatesTags: ['Project'],
        }),

        // Move Project to Folder
        moveProjectToFolder: builder.mutation<ApiResponse, MoveDeckParams>({
            query: ({ projectId, folderId }) => ({
                url: `${BASE_URL}/move-to-folder/${projectId}`,
                method: 'PUT',
                body: { folderId },
            }),
            invalidatesTags: ['Project'],
        }),

        // Export Project to PDF (Download) - OLD: Client-side rendering
        exportProjectToPdf: builder.mutation<ApiResponse, string>({
            query: (projectId) => ({
                url: `${BASE_URL}/export-pdf/${projectId}`,
                method: 'POST',
            }),
        }),

        // Export Deck to PDF (Server-side Puppeteer rendering) - NEW
        exportDeckToPdf: builder.mutation<ApiResponse, { projectId: string; quality?: 'standard' | 'high' }>({
            query: ({ projectId, quality = 'high' }) => ({
                url: `${BASE_URL}/export/${projectId}`,
                method: 'POST',
                body: { format: 'pdf', quality },
            }),
        }),

        // ============ Folder Endpoints ============

        getAllFolders: builder.query<GetFoldersResponse, { parentFolderId?: string } | void>({
            query: (params) => {
                const queryParams = new URLSearchParams();
                if (params?.parentFolderId) queryParams.append('parentFolderId', params.parentFolderId);
                const queryString = queryParams.toString();
                return `${FOLDERS_URL}${queryString ? `?${queryString}` : ''}`;
            },
            providesTags: ['Folder'],
        }),

        createFolder: builder.mutation<ApiResponse, CreateFolderParams>({
            query: ({ name, description, parentFolderId, logo }) => {
                const formData = new FormData();
                formData.append('name', name);
                if (description) formData.append('description', description);
                if (parentFolderId) formData.append('parentFolderId', parentFolderId);
                if (logo) formData.append('logo', logo);

                return {
                    url: `${FOLDERS_URL}/create`,
                    method: 'POST',
                    body: formData,
                    // Don't set Content-Type header - browser will set it with boundary for FormData
                    formData: true,
                };
            },
            invalidatesTags: ['Folder'],
        }),

        updateFolder: builder.mutation<ApiResponse, UpdateFolderParams>({
            query: ({ folderId, name, description, logo }) => {
                const formData = new FormData();
                if (name) formData.append('name', name);
                if (description) formData.append('description', description);
                if (logo) formData.append('logo', logo);

                return {
                    url: `${FOLDERS_URL}/${folderId}`,
                    method: 'PUT',
                    body: formData,
                    formData: true,
                };
            },
            invalidatesTags: ['Folder'],
        }),

        deleteFolder: builder.mutation<ApiResponse, string>({
            query: (folderId) => ({
                url: `${FOLDERS_URL}/${folderId}`,
                method: 'DELETE',
            }),
            invalidatesTags: ['Folder', 'Project'],
        }),

        // ============ Share Endpoints ============

        shareDeck: builder.mutation<ApiResponse, ShareDeckParams>({
            query: ({ projectId, ...body }) => ({
                url: `${SHARE_URL}/${projectId}`,
                method: 'POST',
                body,
            }),
            invalidatesTags: ['ShareVisitors'],
        }),

        getShareVisitors: builder.query<GetShareVisitorsResponse, string>({
            query: (projectId) => `${SHARE_URL}/${projectId}/visitors`,
            providesTags: ['ShareVisitors'],
        }),

        removeShareAccess: builder.mutation<ApiResponse, { projectId: string; email: string }>({
            query: ({ projectId, email }) => ({
                url: `${SHARE_URL}/${projectId}/visitor/${encodeURIComponent(email)}`,
                method: 'DELETE',
            }),
            invalidatesTags: ['ShareVisitors'],
        }),

        // ============ Market Research Endpoints ============
        getMarketResearch: builder.query<GenerateMarketResearchResponse, string>({
            query: (projectId) => `${BASE_URL}/market-research/${projectId}`,
        }),

        getResearchHistory: builder.query<{ success: boolean; data: any[] }, string | void>({
            query: (projectId) => {
                return projectId
                    ? `${BASE_URL}/market-research/history?projectId=${projectId}`
                    : `${BASE_URL}/market-research/history`;
            },
            providesTags: ['ResearchHistory'],
        }),

        generateMarketResearch: builder.mutation<GenerateMarketResearchResponse, GenerateMarketResearchParams>({
            query: (body) => ({
                url: `${BASE_URL}/market-research`,
                method: 'POST',
                body,
            }),
            invalidatesTags: ['ResearchHistory'],
        }),

        // ============ Competitor Analysis Endpoints ============
        generateCompetitorAnalysis: builder.mutation<GenerateCompetitorAnalysisResponse, GenerateCompetitorAnalysisParams>({
            query: (body) => ({
                url: `${BASE_URL}/competitor-analysis`,
                method: 'POST',
                body,
            }),
            invalidatesTags: ['CompetitorHistory'],
        }),

        getCompetitorHistory: builder.query<{ success: boolean; data: any[] }, string | void>({
            query: (projectId) => {
                return projectId
                    ? `${BASE_URL}/competitor-analysis/history?projectId=${projectId}`
                    : `${BASE_URL}/competitor-analysis/history`;
            },
            providesTags: ['CompetitorHistory'],
        }),

        resendShareNotification: builder.mutation<ApiResponse, { projectId: string; email: string }>({
            query: ({ projectId, email }) => ({
                url: `${SHARE_URL}/${projectId}/resend`,
                method: 'POST',
                body: { email },
            }),
        }),

        // ============ Asset Endpoints ============

        getAssets: builder.query<GetAssetsResponse, GetAssetsParams | void>({
            query: (params) => {
                const queryParams = new URLSearchParams();
                if (params?.search) queryParams.append('search', params.search);
                if (params?.type) queryParams.append('type', params.type);
                const queryString = queryParams.toString();
                return `${BASE_URL}/assets${queryString ? `?${queryString}` : ''}`;
            },
            providesTags: ['Asset'],
        }),

        uploadAssets: builder.mutation<ApiResponse, File[]>({
            query: (images) => {
                const formData = new FormData();
                images.forEach((image) => {
                    formData.append('images', image);
                });
                return {
                    url: `${BASE_URL}/assets`,
                    method: 'POST',
                    body: formData,
                    formData: true,
                };
            },
            invalidatesTags: ['Asset'],
        }),

        renameAsset: builder.mutation<ApiResponse, RenameAssetParams>({
            query: ({ assetId, name }) => ({
                url: `${BASE_URL}/assets/item/${assetId}`,
                method: 'PUT',
                body: { filename: name },
            }),
            invalidatesTags: ['Asset'],
        }),

        deleteAsset: builder.mutation<ApiResponse, string>({
            query: (assetId) => ({
                url: `${BASE_URL}/assets/item/${assetId}`,
                method: 'DELETE',
            }),
            invalidatesTags: ['Asset'],
        }),

        deleteMultipleAssets: builder.mutation<ApiResponse, string[]>({
            query: (assetIds) => ({
                url: `${BASE_URL}/assets`,
                method: 'DELETE',
                body: { assetIds },
            }),
            invalidatesTags: ['Asset'],
        }),

        // Save Company Details (Step 1)
        saveCompanyDetails: builder.mutation<ApiResponse, SaveCompanyDetailsParams>({
            query: (body) => ({
                url: `${BASE_URL}/company-details`,
                method: 'POST',
                body,
            }),
            invalidatesTags: ['Project'],
        }),

        // The legacy endpoint now creates the project immediately. Uploaded-file
        // extraction runs only after navigation, in the chat SSE stream.
        createBlankDeck: builder.mutation<CreateBlankDeckResponse, CreateBlankDeckParams | void>({
            query: (body) => {
                if (!body) return { url: `${BASE_URL}/blank-deck`, method: 'POST', body: {} };

                const { files, knowledgeBaseIds, logo, ...rest } = body;
                const hasFiles = files && files.length > 0;
                const hasKBIds = knowledgeBaseIds && knowledgeBaseIds.length > 0;
                const hasLogo = !!logo;

                if (hasFiles || hasKBIds || hasLogo) {
                    const formData = new FormData();
                    if (rest.companyName) formData.append('companyName', rest.companyName);
                    if (rest.companyStage) formData.append('companyStage', rest.companyStage);
                    if (rest.industry) formData.append('industry', rest.industry);
                    if (rest.website) formData.append('website', rest.website);
                    if (rest.numberOfSlides) formData.append('numberOfSlides', rest.numberOfSlides);
                    if (rest.presentationType) formData.append('presentationType', rest.presentationType);
                    if (rest.templateId) formData.append('templateId', rest.templateId);
                    if (rest.documentType) formData.append('documentType', rest.documentType);
                    if (rest.deckType) formData.append('deckType', rest.deckType);
                    if (rest.userPrompt) formData.append('userPrompt', rest.userPrompt);

                    if (hasFiles) {
                        files.forEach((file) => formData.append('files', file));
                    }
                    if (hasKBIds) {
                        knowledgeBaseIds.forEach((id) => formData.append('knowledgeBaseIds', id));
                    }
                    if (hasLogo) {
                        formData.append('logo', logo);
                    }
                    return {
                        url: `${BASE_URL}/blank-deck`,
                        method: 'POST',
                        body: formData,
                        formData: true,
                    };
                }

                return { url: `${BASE_URL}/blank-deck`, method: 'POST', body: rest };
            },
            invalidatesTags: ['Project'],
        }),

        updateCoreContextPrompt: builder.mutation<ApiResponse, UpdateCoreContextPromptParams>({
            query: ({ projectId, prompt, files, knowledgeBaseIds }) => {
                const formData = new FormData();
                formData.append('projectId', projectId);
                formData.append('prompt', prompt);
                if (files) {
                    files.forEach((file) => {
                        formData.append('files', file);
                    });
                }
                if (knowledgeBaseIds) {
                    knowledgeBaseIds.forEach((id) => {
                        formData.append('knowledgeBaseIds', id);
                    });
                }

                return {
                    url: `${BASE_URL}/update-core-context-prompt`,
                    method: 'PUT',
                    body: formData,
                    formData: true,
                };
            },
        }),

        // ============ Analytics Endpoints ============

        getAnalyticsOverview: builder.query<GetAnalyticsOverviewResponse, void>({
            query: () => '/analytics/overview',
            providesTags: ['Analytics'],
        }),

        getProjectAnalytics: builder.query<GetProjectAnalyticsResponse, { projectId: string; startDate?: string; endDate?: string; visitorId?: string }>({
            query: ({ projectId, startDate, endDate, visitorId }) => {
                const queryParams = new URLSearchParams();
                if (startDate) queryParams.append('startDate', startDate);
                if (endDate) queryParams.append('endDate', endDate);
                if (visitorId) queryParams.append('visitorId', visitorId);
                const queryString = queryParams.toString();
                return `/analytics/${projectId}${queryString ? `?${queryString}` : ''}`;
            },
            providesTags: ['Analytics'],
        }),

        // ============ Team Member Endpoints ============

        getTeamMembers: builder.query<GetTeamMembersResponse, string>({
            query: (projectId) => `${BASE_URL}/team/${projectId}`,
            providesTags: ['TeamMembers'],
        }),

        inviteTeamMembers: builder.mutation<ApiResponse, InviteTeamMembersParams>({
            query: ({ projectId, teamMembers }) => ({
                url: `${BASE_URL}/team/${projectId}`,
                method: 'PUT',
                body: { teamMembers },
            }),
            invalidatesTags: ['TeamMembers'],
        }),

        // ============ QA Endpoints ============

        startQaAnswer: builder.mutation<StartQaAnswerResponse, string>({
            query: (projectId) => ({
                url: `${BASE_URL}/qa/start`,
                method: 'POST',
                body: { projectId },
            }),
        }),

        submitQaAnswers: builder.mutation<SubmitQaAnswersResponse, SubmitQaAnswersParams>({
            query: ({ projectId, answers }) => ({
                url: `${BASE_URL}/qa/iteration`,
                method: 'POST',
                body: { projectId, answers },
            }),
        }),

        // ============ Investor QA Endpoints ============

        getInvestorQA: builder.query<GetInvestorQAResponse, string>({
            query: (projectId) => `${BASE_URL}/investor-qa/${projectId}`,
        }),

        // ============ Outline Endpoints ============

        generateOutline: builder.mutation<GenerateOutlineResponse, GenerateOutlineParams>({
            query: ({ projectId, regenerate, tone, language }) => ({
                url: `${BASE_URL}/outline/generate`,
                method: 'POST',
                body: { projectId, regenerate, tone, language },
            }),
            invalidatesTags: ['Project'],
        }),

        getOutline: builder.query<GetOutlineResponse, string>({
            query: (projectId) => `${BASE_URL}/outline/${projectId}`,
            providesTags: (_result, _error, projectId) => [{ type: 'Project', id: projectId }],
        }),

        updateOutline: builder.mutation<UpdateOutlineResponse, UpdateOutlineParams>({
            query: ({ projectId, slides, narrativeArc, language, tone }) => ({
                url: `${BASE_URL}/outline/${projectId}`,
                method: 'PUT',
                body: { slides, narrativeArc, language, tone },
            }),
            invalidatesTags: ['Project'],
        }),

        // AI Slide Modification
        aiModifySlide: builder.mutation<AiModifySlideResponse, AiModifySlideParams>({
            query: ({ projectId, slideIndex, prompt, currentContent }) => ({
                url: `${BASE_URL}/outline/ai-modify-slide`,
                method: 'POST',
                body: { projectId, slideIndex, prompt, currentContent },
            }),
        }),

        // Translate Single Slide (for incremental regeneration)
        translateSlide: builder.mutation<TranslateSlideResponse, TranslateSlideParams>({
            query: ({ projectId, slideIndex, currentContent, language, tone }) => ({
                url: `${BASE_URL}/outline/translate-slide`,
                method: 'POST',
                body: { projectId, slideIndex, currentContent, language, tone },
            }),
        }),

        // ============ Slide Generation Endpoints ============

        // Generate HTML slides from outline
        generateSlides: builder.mutation<GenerateSlidesResponse, GenerateSlidesParams>({
            query: ({ projectId, regenerate }) => ({
                url: `${BASE_URL}/slides/generate`,
                method: 'POST',
                body: { projectId, regenerate },
            }),
            invalidatesTags: ['Project'],
        }),

        // Get existing generated slides
        getGeneratedSlides: builder.query<GetGeneratedSlidesResponse, string>({
            query: (projectId) => `${BASE_URL}/slides/${projectId}`,
            providesTags: (_result, _error, projectId) => [{ type: 'Project', id: projectId }],
        }),

        // Generate single slide from AI prompt (supports images, research, context)
        generateSlideFromPrompt: builder.mutation<GenerateSlideFromPromptResponse, GenerateSlideFromPromptParams>({
            query: ({ projectId, prompt, imageUrl, imageMetadata, options }) => ({
                url: `${BASE_URL}/slides/generate-from-prompt`,
                method: 'POST',
                body: { projectId, prompt, imageUrl, imageMetadata, options },
            }),
        }),

        // AI Edit existing slide HTML content
        aiEditSlide: builder.mutation<AiEditSlideResponse, AiEditSlideParams>({
            query: ({ projectId, slideId, slideIndex, prompt, currentHtmlContent, editType, chipId, imageUrl, imageMetadata }) => ({
                url: `${BASE_URL}/slides/ai-edit`,
                method: 'POST',
                body: { projectId, slideId, slideIndex, prompt, currentHtmlContent, editType, chipId, imageUrl, imageMetadata },
            }),
        }),

        // Get suggestion chips for AI edit UI
        getSuggestionChips: builder.query<GetSuggestionChipsResponse, void>({
            query: () => `${BASE_URL}/slides/suggestion-chips`,
        }),

        // Change slide layout (hybrid: template + AI)
        changeSlideLayout: builder.mutation<ChangeSlideLayoutResponse, ChangeSlideLayoutParams>({
            query: ({ projectId, slideId, slideIndex, targetLayoutId, currentHtmlContent }) => ({
                url: `${BASE_URL}/slides/change-layout`,
                method: 'POST',
                body: { projectId, slideId, slideIndex, targetLayoutId, currentHtmlContent },
            }),
        }),

        // Upload background image to S3
        uploadBackgroundImage: builder.mutation<UploadBackgroundImageResponse, UploadBackgroundImageParams>({
            query: ({ projectId, slideId, image }) => {
                const formData = new FormData();
                formData.append('projectId', projectId);
                formData.append('slideId', slideId);
                formData.append('image', image);

                return {
                    url: `${BASE_URL}/slides/upload-background-image`,
                    method: 'POST',
                    body: formData,
                };
            },
        }),

        // Generate background image with AI
        generateBackgroundImage: builder.mutation<GenerateBackgroundImageResponse, GenerateBackgroundImageParams>({
            query: ({ projectId, slideId, slideIndex }) => ({
                url: `${BASE_URL}/slides/generate-background-image`,
                method: 'POST',
                body: { projectId, slideId, slideIndex },
            }),
        }),

        // Generate AI image for modal preview (returns base64 for preview, no S3 upload)
        generateAiImage: builder.mutation<GenerateAiImageResponse, GenerateAiImageParams>({
            query: ({ projectId, prompt, aspectRatio, style }) => ({
                url: `${BASE_URL}/slides/generate-ai-image`,
                method: 'POST',
                body: { projectId, prompt, aspectRatio, style },
            }),
        }),

        // Confirm AI image (uploads base64 to S3 when user clicks "Add to Slide")
        confirmAiImage: builder.mutation<ConfirmAiImageResponse, ConfirmAiImageParams>({
            query: ({ projectId, imageBase64 }) => ({
                url: `${BASE_URL}/slides/confirm-ai-image`,
                method: 'POST',
                body: { projectId, imageBase64 },
            }),
        }),

        // Apply background image to slide HTML
        applyBackgroundImage: builder.mutation<ApplyBackgroundImageResponse, ApplyBackgroundImageParams>({
            query: ({ projectId, slideId, slideIndex, imageUrl, currentHtmlContent }) => ({
                url: `${BASE_URL}/slides/apply-background-image`,
                method: 'POST',
                body: { projectId, slideId, slideIndex, imageUrl, currentHtmlContent },
            }),
        }),

        // Change background color (CSS variable)
        changeBackgroundColor: builder.mutation<ChangeBackgroundColorResponse, ChangeBackgroundColorParams>({
            query: ({ projectId, slideId, slideIndex, color }) => ({
                url: `${BASE_URL}/slides/change-background-color`,
                method: 'POST',
                body: { projectId, slideId, slideIndex, color },
            }),
        }),

        // Change theme (colors + fonts) for all slides or single slide
        changeTheme: builder.mutation<ChangeThemeResponse, ChangeThemeParams>({
            query: (body) => ({
                url: `${BASE_URL}/slides/change-theme`,
                method: 'POST',
                body,
            }),
            invalidatesTags: ['Project'],
        }),

        // Save/update slides (for editor changes)
        saveSlides: builder.mutation<ApiResponse, { projectId: string; slides: Array<{ id: string; title: string; htmlContent: string; order?: number }> }>({
            query: ({ projectId, slides }) => ({
                url: `${BASE_URL}/save-slides/${projectId}`,
                method: 'PUT',
                body: { slides },
            }),
            invalidatesTags: (_result, _error, { projectId }) => [{ type: 'Project', id: projectId }],
        }),

        saveMarketResearch: builder.mutation<SaveMarketResearchResponse, SaveMarketResearchParams>({
            query: ({ projectId, data }) => ({
                url: `${BASE_URL}/research/save/${projectId}`,
                method: 'POST',
                body: data,
            }),
            invalidatesTags: (_result, _error, { projectId }) => [{ type: 'Project', id: projectId }, { type: 'ResearchHistory' }],
        }),

        deleteSlide: builder.mutation<ApiResponse, { projectId: string; slideId: string }>({
            query: ({ projectId, slideId }) => ({
                url: `${BASE_URL}/slides/${projectId}/slide/${slideId}`,
                method: 'DELETE',
            }),

            invalidatesTags: (_result, _error, { projectId }) => [
                { type: 'Project', id: projectId },
            ],

            async onQueryStarted({ projectId }, { dispatch, queryFulfilled }) {
                try {
                    const { data: result } = await queryFulfilled;

                    if (result?.success && result?.data?.slides) {
                        dispatch(
                            pitchDeckApi.util.updateQueryData(
                                'getStreamState',
                                projectId,
                                (draft) => {
                                    if (draft.success && draft.data) {
                                        draft.data.slides = result.data.slides;
                                    }
                                }
                            )
                        );
                    }
                } catch (err) {
                    console.error(
                        'Failed to update getStreamState cache after slide deletion:',
                        err
                    );
                }
            },
        }),


        // ============ Platform Template Endpoints ============

        // List all templates (public, no auth required)
        getTemplates: builder.query<GetTemplatesResponse, string | void>({
            query: (category) => {
                const url = '/templates';
                return category ? `${url}?category=${category}` : url;
            },
            providesTags: ['PlatformTemplate'],
        }),

        // Assign template to project
        assignTemplateToProject: builder.mutation<ApiResponse, AssignTemplateParams>({
            query: ({ projectId, templateId }) => ({
                url: `${BASE_URL}/update-project/${projectId}`,
                method: 'PUT',
                body: { templateId },
            }),
            invalidatesTags: (_result, _error, { projectId }) => [{ type: 'Project', id: projectId }],
        }),

        // ============ Blank Deck Slide Endpoints ============

        // Get blank deck slides
        getBlankDeckSlides: builder.query<GetGeneratedSlidesResponse, string>({
            query: (projectId) => `${BLANK_DECK_URL}/${projectId}/slides`,
            providesTags: (_result, _error, projectId) => [{ type: 'Project', id: projectId }],
            transformResponse: (response: { success: boolean; statusCode: number; data: { slides: GeneratedSlide[] }; message: string }): GetGeneratedSlidesResponse => ({
                success: response.success,
                statusCode: response.statusCode,
                data: {
                    exists: response.data.slides && response.data.slides.length > 0,
                    slides: response.data.slides || [],
                },
                message: response.message,
            }),
        }),

        // Update single blank deck slide
        updateBlankDeckSlide: builder.mutation<ApiResponse, { projectId: string; slideId: string; htmlContent?: string; title?: string; speakerNotes?: string }>({
            query: ({ projectId, slideId, ...body }) => ({
                url: `${BLANK_DECK_URL}/${projectId}/slides/${slideId}`,
                method: 'PUT',
                body,
            }),
            invalidatesTags: (_result, _error, { projectId }) => [{ type: 'Project', id: projectId }],
        }),

        // AI edit blank deck slide
        aiEditBlankDeckSlide: builder.mutation<AiEditSlideResponse, AiEditSlideParams>({
            query: ({ projectId, slideId, prompt, currentHtmlContent, imageUrl, imageMetadata, attachedFiles }) => {
                const hasFiles = attachedFiles && attachedFiles.length > 0;

                if (hasFiles) {
                    const formData = new FormData();
                    formData.append('prompt', prompt);
                    formData.append('currentHtml', currentHtmlContent);
                    if (imageUrl) formData.append('imageUrl', imageUrl);
                    if (imageMetadata) formData.append('imageMetadata', JSON.stringify(imageMetadata));
                    attachedFiles!.forEach((f) => {
                        formData.append('files', f.file, f.name);
                    });
                    return {
                        url: `${BLANK_DECK_URL}/${projectId}/slides/${slideId}/ai-edit`,
                        method: 'POST',
                        body: formData,
                        formData: true,
                    };
                }

                return {
                    url: `${BLANK_DECK_URL}/${projectId}/slides/${slideId}/ai-edit`,
                    method: 'POST',
                    body: { prompt, currentHtml: currentHtmlContent, imageUrl, imageMetadata },
                };
            },
            transformResponse: (response: { success: boolean; statusCode: number; data: { editedHtml: string; changes?: string }; message: string }): AiEditSlideResponse => ({
                success: response.success,
                statusCode: response.statusCode,
                data: {
                    htmlContent: response.data.editedHtml,
                    changesSummary: response.data.changes,
                },
                message: response.message,
            }),
        }),

        // Change blank deck slide layout
        changeBlankDeckSlideLayout: builder.mutation<ChangeSlideLayoutResponse, ChangeSlideLayoutParams>({
            query: ({ projectId, slideId, targetLayoutId, currentHtmlContent }) => ({
                url: `${BLANK_DECK_URL}/${projectId}/slides/${slideId}/layout`,
                method: 'POST',
                body: { layoutType: targetLayoutId, currentHtml: currentHtmlContent },
            }),
        }),

        // Change blank deck slide theme
        changeBlankDeckSlideTheme: builder.mutation<ApiResponse, { projectId: string; slideId: string; themeConfig: any }>({
            query: ({ projectId, slideId, themeConfig }) => ({
                url: `${BLANK_DECK_URL}/${projectId}/slides/${slideId}/theme`,
                method: 'POST',
                body: { themeConfig },
            }),
        }),

        // Get blank deck outline
        getBlankDeckOutline: builder.query<GetOutlineResponse, string>({
            query: (projectId) => `${BLANK_DECK_URL}/${projectId}/outline`,
            providesTags: (_result, _error, projectId) => [{ type: 'Project', id: projectId }],
            transformResponse: (response: { statusCode: number; data: { outline: any }; message: string }): GetOutlineResponse => ({
                success: true,
                statusCode: response.statusCode,
                data: {
                    exists: !!response.data?.outline,
                    outline: response.data?.outline || null,
                    projectInfo: { name: '', industry: '', stage: '' },
                },
                message: response.message,
            }),
        }),

        // ============ Comments Endpoints ============

        getDeckComments: builder.query<GetDeckCommentsResponse, string>({
            query: (projectId) => `/public/project/${projectId}/comments`,
            providesTags: ['DeckComments'],
        }),

        addDeckComment: builder.mutation<AddDeckCommentResponse, { projectId: string; visitorId: string; text: string }>({
            query: ({ projectId, ...body }) => ({
                url: `/public/project/${projectId}/comments`,
                method: 'POST',
                body,
            }),
            invalidatesTags: ['DeckComments'],
        }),

        editDeckComment: builder.mutation<EditDeckCommentResponse, { projectId: string; commentId: string; visitorId: string; text: string }>({
            query: ({ projectId, commentId, ...body }) => ({
                url: `/public/project/${projectId}/comments/${commentId}`,
                method: 'PATCH',
                body,
            }),
            invalidatesTags: ['DeckComments'],
        }),

        deleteDeckComment: builder.mutation<DeleteDeckCommentResponse, { projectId: string; commentId: string; visitorId: string }>({
            query: ({ projectId, commentId, visitorId }) => ({
                url: `/public/project/${projectId}/comments/${commentId}`,
                method: 'DELETE',
                body: { visitorId },
            }),
            invalidatesTags: ['DeckComments'],
        }),

        ownerDeleteDeckComment: builder.mutation<DeleteDeckCommentResponse, { projectId: string; commentId: string }>({
            query: ({ projectId, commentId }) => ({
                url: `/project/${projectId}/comments/${commentId}`,
                method: 'DELETE',
            }),
            invalidatesTags: ['DeckComments'],
        }),

        reportIssue: builder.mutation<ReportIssueResponse, ReportIssueParams>({
            query: ({ projectId, reportType, comment, slideIndex, screenshots }) => {
                if (screenshots && screenshots.length > 0) {
                    const formData = new FormData();
                    formData.append('reportType', reportType);
                    formData.append('comment', comment);
                    if (slideIndex !== undefined) formData.append('slideIndex', slideIndex.toString());

                    screenshots.forEach((file) => {
                        formData.append('screenshots', file);
                    });

                    return {
                        url: `/blank-deck/projects/${projectId}/report`,
                        method: 'POST',
                        body: formData,
                        formData: true,
                    };
                }

                return {
                    url: `/blank-deck/projects/${projectId}/report`,
                    method: 'POST',
                    body: { reportType, comment, slideIndex },
                };
            },
        }),
    }),
});

export const {
    // Project hooks
    useGetAllProjectsQuery,
    useGetProjectQuery,
    useUpdateProjectMutation,
    useSetThemeStepMutation,
    useDeleteProjectMutation,
    useBulkDeleteProjectsMutation,
    useMoveProjectToFolderMutation,
    useExportProjectToPdfMutation,
    useExportDeckToPdfMutation,
    useGetStreamStateQuery,
    useGetChatHistoryQuery,
    // Folder hooks
    useGetAllFoldersQuery,
    useCreateFolderMutation,
    useUpdateFolderMutation,
    useDeleteFolderMutation,
    // Share hooks
    useShareDeckMutation,
    useGetShareVisitorsQuery,
    useRemoveShareAccessMutation,
    useResendShareNotificationMutation,
    // Asset hooks
    useGetAssetsQuery,
    useUploadAssetsMutation,
    useRenameAssetMutation,
    useDeleteAssetMutation,
    useDeleteMultipleAssetsMutation,
    useSaveCompanyDetailsMutation,
    useUpdateCoreContextPromptMutation,
    // Analytics hooks
    useGetAnalyticsOverviewQuery,
    useGetProjectAnalyticsQuery,
    // Team Member hooks
    useGetTeamMembersQuery,
    useInviteTeamMembersMutation,
    // QA hooks
    useStartQaAnswerMutation,
    useSubmitQaAnswersMutation,
    // Investor QA hooks
    useGetInvestorQAQuery,
    // Outline hooks
    useGenerateOutlineMutation,
    useGetOutlineQuery,
    useUpdateOutlineMutation,
    useAiModifySlideMutation,
    useTranslateSlideMutation,
    // Slide Generation hooks
    useGenerateSlidesMutation,
    useGetGeneratedSlidesQuery,
    useGenerateSlideFromPromptMutation,
    useAiEditSlideMutation,
    useGetSuggestionChipsQuery,
    useChangeSlideLayoutMutation,
    useUploadBackgroundImageMutation,
    useGenerateBackgroundImageMutation,
    useGenerateAiImageMutation,
    useConfirmAiImageMutation,
    useApplyBackgroundImageMutation,
    useChangeBackgroundColorMutation,
    useChangeThemeMutation,
    useSaveSlidesMutation,
    useDeleteSlideMutation,
    useCreateBlankDeckMutation,

    useGenerateMarketResearchMutation,
    useGetMarketResearchQuery,
    useGetResearchHistoryQuery,
    useGenerateCompetitorAnalysisMutation,
    useGetCompetitorHistoryQuery,
    useSaveMarketResearchMutation,
    // Template hooks
    useGetTemplatesQuery,
    useAssignTemplateToProjectMutation,
    // Blank Deck Slide hooks
    useGetBlankDeckSlidesQuery,
    useUpdateBlankDeckSlideMutation,
    useAiEditBlankDeckSlideMutation,
    useChangeBlankDeckSlideLayoutMutation,
    useChangeBlankDeckSlideThemeMutation,
    useGetBlankDeckOutlineQuery,
    // Comment hooks
    useGetDeckCommentsQuery,
    useAddDeckCommentMutation,
    useEditDeckCommentMutation,
    useDeleteDeckCommentMutation,
    useOwnerDeleteDeckCommentMutation,
    useReportIssueMutation,
} = pitchDeckApi;

// Export types for use in components
export type {
    Folder,
    Asset,
    AnalyticsOverviewItem,
    SlideEngagementData,
    ProjectAnalyticsSummary,
    VisitorInsight,
    VisitorLocation,
    TeamMember,
    QAQuestion,
    QAAnswer,
    QAStats,
    StartQaAnswerResponse,
    // Outline types
    Outline,
    OutlineSlide,
    OutlineMeta,
    OutlineDataSources,
    OutlineSlideSuggestion,
    OutlineRecommendation,
    // Citation types
    Citation,
    CitationSummary,
    ResearchUtilization,
    NarrativeMetadata,
    GenerateOutlineParams,
    GenerateOutlineResponse,
    GetOutlineResponse,
    UpdateOutlineParams,
    UpdateOutlineResponse,
    AiModifySlideParams,
    AiModifySlideResponse,
    TranslateSlideParams,
    TranslateSlideResponse,
    GenerateSlideFromPromptParams,
    GenerateSlideFromPromptResponse,
    // AI Edit Slide types
    AiEditSlideParams,
    AiEditSlideResponse,
    EditType,
    ImageMetadata,
    SuggestionChip,
    GetSuggestionChipsResponse,
    // Change Slide Layout types
    ChangeSlideLayoutParams,
    ChangeSlideLayoutResponse,
    // Slide Generation types
    GeneratedSlide,
    SlideGenerationMeta,
    GenerateSlidesParams,
    GenerateSlidesResponse,
    GetGeneratedSlidesResponse,
    CreateBlankDeckResponse,
    // AI Image Generation types
    GenerateAiImageParams,
    GenerateAiImageResponse,
    ConfirmAiImageParams,
    ConfirmAiImageResponse,
    // Template types
    GetTemplatesResponse,
    AssignTemplateParams,
    // Comment types
    DeckComment,
    GetDeckCommentsResponse,
    AddDeckCommentResponse,
    EditDeckCommentResponse,
    DeleteDeckCommentResponse,
    ReportIssueParams,
    ReportIssueResponse,
};
